import React, { useState, useEffect } from 'react';
import { useMobile } from '../hooks/useMobile';
import { BACKEND } from '../utils/api';
import { LogoIcon } from '../assets/LogoIcon';

interface LoginProps {
  onLogin: (token: string) => void;
}

export function Login({ onLogin }: LoginProps) {
  const [user, setUser] = useState('admin');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [dashboardTitle, setDashboardTitle] = useState('K3s Dashboard');
  const [dashboardSubtitle, setDashboardSubtitle] = useState('Remote Cluster Monitor');
  const mobile = useMobile();

  useEffect(() => {
    // Fetch public dashboard title from settings
    fetch(`${BACKEND}/api/health`)
      .then(() => fetch(`${BACKEND}/api/settings/public`))
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.dashboardTitle) setDashboardTitle(data.dashboardTitle);
        if (data?.dashboardSubtitle) setDashboardSubtitle(data.dashboardSubtitle);
      })
      .catch(() => {});
  }, []);

  async function submit() {
    if (!user.trim() || !pass) { setErr('Please enter username and password'); return; }
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${BACKEND}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user, password: pass }),
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('k3s_token', data.token);
        onLogin(data.token);
      } else {
        setErr(data.error || 'Login failed');
      }
    } catch {
      setErr('Cannot reach server');
    } finally {
      setLoading(false);
    }
  }

  const inp: React.CSSProperties = {
    background: '#0f172a',
    border: '1px solid #334155',
    color: '#e2e8f0',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: mobile ? 16 : 0,
    }}>
      <div style={{
        background: '#1e293b',
        borderRadius: 16,
        padding: mobile ? 24 : 44,
        width: mobile ? '100%' : 380,
        maxWidth: 420,
        border: '1px solid #334155',
        boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
      }}>
        {/* Logo + Title */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <LogoIcon size={52} />
          </div>
          <h1 style={{ margin: '0 0 6px', color: '#f1f5f9', fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
            {dashboardTitle}
          </h1>
          <div style={{ color: '#64748b', fontSize: 13 }}>{dashboardSubtitle}</div>
        </div>

        {/* Username */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 6, fontWeight: 600, letterSpacing: 0.3 }}>
            USERNAME
          </label>
          <input
            style={inp}
            value={user}
            autoComplete="username"
            onChange={e => setUser(e.target.value)}
            onFocus={e => (e.target.style.borderColor = '#3b82f6')}
            onBlur={e => (e.target.style.borderColor = '#334155')}
          />
        </div>

        {/* Password with toggle */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 6, fontWeight: 600, letterSpacing: 0.3 }}>
            PASSWORD
          </label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inp, paddingRight: 42 }}
              type={showPass ? 'text' : 'password'}
              value={pass}
              autoComplete="current-password"
              onChange={e => setPass(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submit()}
              onFocus={e => (e.target.style.borderColor = '#3b82f6')}
              onBlur={e => (e.target.style.borderColor = '#334155')}
            />
            <button
              type="button"
              onClick={() => setShowPass(s => !s)}
              style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                color: '#64748b', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center',
              }}
              title={showPass ? 'Hide password' : 'Show password'}
            >
              {showPass ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {err && (
          <div style={{
            color: '#fca5a5', fontSize: 13, marginBottom: 16, textAlign: 'center',
            background: '#7f1d1d33', borderRadius: 8, padding: '8px 12px', border: '1px solid #7f1d1d',
          }}>
            {err}
          </div>
        )}

        <button
          onClick={submit}
          disabled={loading}
          style={{
            width: '100%',
            background: loading ? '#1e40af' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '12px',
            fontSize: 14,
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            letterSpacing: 0.3,
            transition: 'opacity 0.15s',
          }}
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </div>
    </div>
  );
}
